import React from 'react';
import dayjs from 'dayjs';
import { ApptSegment, SegmentedAppointment } from '../segments';
import { jobStatusColor, readableTextColor } from '../statusColors';
import { initialsFromName } from '../useSchedulingData';

export interface ApptBlockProps {
  appt: SegmentedAppointment;
  segment: ApptSegment;
  onOpen: (id: number) => void;
  draggable?: boolean;
  /** The assigned technician's `calendar_color` (resolved by the caller from
   *  the `techs` list, since ApptBlock itself has no tech lookup). Renders as
   *  the block's left edge; falls back to a neutral grey when unassigned or
   *  the tech has no color set. */
  techColor?: string;
  /** The assigned technician's initials, resolved by the caller from the same
   *  `techs` list. Only used when no `techColor` resolved — see the badge note
   *  below. Optional: ApptBlock falls back to the appointment's own technician
   *  record when the caller can't resolve one (e.g. an inactive tech who is no
   *  longer in the active-tech list). */
  techInitials?: string;
}

// Neutral edge color used when the appointment has no assigned technician or
// the technician has no `calendar_color` set.
const NO_TECH_COLOR = '#d9d9d9';

// `customer_name` is a synthetic shorthand used throughout the Scheduling
// test suite (DayGrid/WeekGrid/MobileWeek fixtures predate the real
// `customer` object shape) — kept as the top-priority fallback so those
// suites keep passing. Real API data never sets it, so production always
// falls through to the composed name below.
function composeCustomerLabel(appt: SegmentedAppointment): string {
  const synthetic = (appt as any).customer_name;
  if (synthetic) return synthetic;
  const c = appt.customer;
  const composed = c?.company_name || [c?.first_name, c?.last_name].filter(Boolean).join(' ');
  return composed || appt.title || 'Appointment';
}

function composeVehicleLabel(appt: SegmentedAppointment): string {
  const v = appt.vehicle;
  if (!v) return '';
  return [v.year, v.make, v.model].filter(Boolean).join(' ');
}

function composeTechLabel(appt: SegmentedAppointment): string {
  const t = appt.technician;
  if (!t) return '';
  return [t.first_name, t.last_name].filter(Boolean).join(' ');
}

// Prefer the human "#EST-…"/"#WO…" token already embedded in the title
// (e.g. "Service — Estimate #EST-TEST-1783864012119"); fall back to the raw
// estimate/work-order id. Kept for the hover tooltip — the reference itself
// no longer renders inline on the 2-line block (Task 6).
function composeReference(appt: SegmentedAppointment): string {
  const match = appt.title?.match(/#\S+/);
  if (match) return match[0];
  if (appt.estimate_id) return `Est #${appt.estimate_id}`;
  if (appt.work_order_id) return `WO #${appt.work_order_id}`;
  return '';
}

// The block's 2nd line: a short human service description, stripped of the
// "#EST-…"/"#WO…" reference token (and any "— Estimate"/"— WO" separator
// left dangling once the token is gone) so the reference isn't duplicated
// inline — it already lives in composeReference()/the tooltip.
function composeServiceLabel(appt: SegmentedAppointment): string {
  const raw = appt.title || '';
  const withoutToken = raw.replace(/#\S+/g, '').trim();
  const withoutTrailingSeparator = withoutToken
    .replace(/[—–-]\s*(estimate|work order|wo)?\s*$/i, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return withoutTrailingSeparator || appt.appointment_type || 'Service';
}

// Overall finish across ALL days of a multi-day appointment — the owner's
// main complaint was the block only ever showed the start. Prefers the
// appointment's own end_time, falling back to the last segment's end.
function overallFinishLabel(appt: SegmentedAppointment): string {
  const segs = appt.segments;
  const lastSeg = segs && segs.length ? segs[segs.length - 1] : null;
  const finish = appt.end_time || lastSeg?.end;
  if (!finish) return '';
  return dayjs(finish).format('ddd h:mma');
}

export function ApptBlock({ appt, segment, onOpen, draggable, techColor, techInitials }: ApptBlockProps) {
  const { color } = jobStatusColor(appt);
  const textColor = readableTextColor(color);
  const edgeColor = techColor || NO_TECH_COLOR;
  const customerLabel = composeCustomerLabel(appt);
  const serviceLabel = composeServiceLabel(appt);
  const vehicleLabel = composeVehicleLabel(appt);
  const techLabel = composeTechLabel(appt);
  const reference = composeReference(appt);
  const isMultiDay = segment.segCount > 1;
  const continuesToNext = isMultiDay && segment.segIndex < segment.segCount - 1;
  const continuedFromPrev = isMultiDay && segment.segIndex > 0;

  // WP-01: an assigned technician who has never set a `calendar_color` used to
  // render the SAME neutral grey edge as an unassigned appointment, so "nobody
  // owns this" and "Cy owns this" were pixel-identical. That is worst on Week
  // view, where every technician's blocks share one day column and the edge
  // colour is the only ownership cue there is. Rather than invent a colour —
  // which could collide with a real one a technician later picks, and would bake
  // a palette in ahead of the per-shop colour model — name the owner: show their
  // initials, and ONLY when no colour resolved, so coloured blocks are visually
  // untouched. Falls back to the appointment's own technician record so an
  // appointment on an inactive technician (absent from the active-tech list the
  // caller resolves against) is still attributed rather than reading as
  // unassigned. Genuinely unassigned appointments resolve to '' and stay bare,
  // which is what makes the two states distinguishable.
  const ownerInitials = techColor ? '' : (techInitials || initialsFromName(techLabel));

  // The visible block is now just 2 lines (customer, service) — everything
  // else (vehicle/tech/reference/exact times/multi-day finish) still rides
  // along in the hover tooltip so the detail isn't lost, just decluttered.
  const finishLabel = isMultiDay ? overallFinishLabel(appt) : '';
  const fullStart = dayjs(appt.start_time).format('ddd h:mma');
  const fullEnd = dayjs(appt.end_time || segment.end).format('ddd h:mma');
  const tooltip = [
    customerLabel,
    serviceLabel,
    vehicleLabel,
    reference,
    techLabel ? `Tech: ${techLabel}` : 'Unassigned',
    `${fullStart} → ${fullEnd}`,
    isMultiDay ? `${segment.hours}h this day${finishLabel ? ` · ends ${finishLabel}` : ''}` : '',
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <div
      role="button"
      tabIndex={0}
      draggable={!!draggable}
      onClick={() => onOpen(appt.id!)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen(appt.id!);
      }}
      data-testid="appt-block"
      data-appt-block
      title={tooltip}
      style={{
        height: '100%',
        boxSizing: 'border-box',
        borderLeftWidth: 3,
        borderLeftStyle: 'solid',
        borderLeftColor: edgeColor,
        backgroundColor: color,
        color: textColor,
        borderRadius: 4,
        padding: '2px 6px',
        cursor: 'pointer',
        overflow: 'hidden',
        fontSize: 12,
        lineHeight: 1.3,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {continuedFromPrev && (
          <span aria-label="continued from previous day" title="continued from previous day">
            &#8592;
          </span>
        )}
        {ownerInitials && (
          <span
            data-testid="appt-block-tech-initials"
            aria-label={techLabel ? `Assigned to ${techLabel}` : 'Assigned technician'}
            title={techLabel ? `Assigned to ${techLabel} (no calendar color set)` : 'Assigned technician'}
            style={{
              flexShrink: 0,
              fontSize: 9,
              fontWeight: 700,
              lineHeight: 1,
              padding: '1px 3px',
              borderRadius: 3,
              border: `1px solid ${textColor}`,
              opacity: 0.75,
              letterSpacing: 0.3,
            }}
          >
            {ownerInitials}
          </span>
        )}
        <span
          style={{
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {customerLabel}
        </span>
        {continuesToNext && (
          <span aria-label="continues to next day" title="continues to next day">
            &#8594;
          </span>
        )}
      </div>
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {serviceLabel}
      </div>
    </div>
  );
}

export default ApptBlock;
